import { languageNames } from '../config'

export type Extractor = {
  link: string;
  title: string;
  name: string;
  category: 'dictionaries' | 'definitions-source' | 'definitions-target';
  linkOnly?: boolean;
};

export const hasDefinitions = (source: string): boolean => {
  return true
}

export function getQueryLinks(source: string, target: string, query: string): Extractor[] {
  const dic: Record<string, string> = {
    hu: 'hun',
    en: 'eng',
    de: 'ger',
    it: 'ita',
    fr: 'fre',
    pl: 'pol',
    bg: 'bul',
    zh: 'cmn',
  }

  const getSztakiQuery = (name: string, query: string, source: string, target: string): string => {
    return 'https://szotar.sztaki.hu/search?viewMode=full&langprefix=&u=0&searchWord=' + query +
      '&ignoreAccents=1&langcode=' + source +
      '&fromlang=' + dic[source] +
      '&tolang=' + dic[target] +
      '&searchMode=WORD_PREFIX'
  }

  const getGoogleQuery = (query: string, source: string, target: string): string => {
    return 'https://translate.google.com/#' + source + '/' + target + '/' + query.replace(/\//g, '%2F')
  }

  const getGlosbeQuery = (query: string, source: string, target: string): string => {
    return 'https://glosbe.com/' + source + '/' + target + '/' + query
  }

  let extractors: Extractor[] = [
    { name: 'self', title: '', link: '', category: 'dictionaries' },
  ]

  if ((source === 'hu' && dic[target]) || (target === 'hu' && dic[source])) {
    extractors.push({
      name: 'sztaki',
      title: 'Sztaki Szótár',
      link: getSztakiQuery('sztaki', query, source, target),
      category: 'dictionaries',
    })
  }
  if ((source === 'es' && target === 'en') || (target === 'en' && source === 'es')) {
    extractors.push({
      name: 'spanishdict',
      title: 'SpanishDict',
      link: `https://www.spanishdict.com/translate/${query}`,
      category: 'dictionaries',
      linkOnly: true,
    })
  }

  extractors.push(
    {
      name: 'google-translate',
      title: 'Google Translate',
      link: getGoogleQuery(query, source, target),
      category: 'dictionaries',
      linkOnly: true,
    },
    {
      name: 'glosbe',
      title: 'Glosbe',
      link: getGlosbeQuery(query, source, target),
      category: 'dictionaries',
      linkOnly: true,
    },
  )


  extractors = extractors
    .concat(getDefinitions(source, query).map(d => (
      { category: 'definitions-source', linkOnly: true, ...d }
    )))
    .concat(getDefinitions(target, query).map(d => (
      { category: 'definitions-target', linkOnly: true, ...d }
    )))

  const queryIndicator = query ? ' - ' + query : ''
  if (queryIndicator) {
    extractors = extractors.map(e => ({ ...e, title: e.title + queryIndicator }))
  }
  return extractors
}

const getDefinitions = (source: string, query: string): any[] => {
  const definitions = []

  if (source === 'de') {
    definitions.push({
      name: 'duden',
      title: 'Duden.de',
      link: 'https://www.duden.de/suchen/dudenonline/' + query,
    })
  } else if (source === 'en') {
    definitions.push({
      name: 'merriam-webster',
      title: 'Merriam-Webster',
      link: 'https://www.merriam-webster.com/dictionary/' + query,
    })
  } else if (source === 'eo') {
    definitions.push({
      name: 'vortaro',
      title: 'Vortaro.net',
      link: `https://www.vortaro.net/#${query}_kd`,
    })
  } else if (source === 'es') {
    // definitions.push({
    //   name: 'conjugacion-es',
    //   title: 'Conjugación.es',
    //   link: `https://www.conjugacion.es/del/verbo/${query}.php`,
    // })
    definitions.push({
      name: 'spanishdict',
      title: 'SpanishDict - conjugation',
      link: `https://www.spanishdict.com/conjugate/${query}`,
    })
    definitions.push({
      name: 'rae',
      title: 'Real Academia Española',
      link: `https://dle.rae.es/${query}`,
    })
  } else if (source === 'fr') {
    definitions.push({
      name: 'lefigaro',
      title: 'Le Figaro Conjugaison',
      link: `https://leconjugueur.lefigaro.fr/french/verb/${query}.html`,
    })
    definitions.push({
      name: 'lerobert',
      title: 'Le Robert - Dico en ligne',
      link: `https://dictionnaire.lerobert.com/definition/${query}`,
    })
  } else if (source === 'hu') {
    definitions.push({
      name: 'szojelentese',
      title: 'SzoJelentese',
      link: 'https://' + query + '.szojelentese.com',
      linkOnly: false,
    })
  } else if (source === 'it') {
    definitions.push({
      name: 'coniugazione-it',
      title: 'Coniugazione.it',
      link: `https://www.coniugazione.it/verbo/${query}.php`,
    })
    definitions.push({
      name: 'treccani',
      title: 'Treccani.it',
      link: `https://www.treccani.it/enciclopedia/ricerca/${query}/?search=${query}`,
    })
  } else if (source === 'ja') {
    definitions.push({
      name: 'jisho',
      title: 'Jisho.org (meaning)',
      link: `https://jisho.org/search/${query}`,
    })
    definitions.push({
      name: 'jisho',
      title: 'Jisho.org (kanji stroke order)',
      link: `https://jisho.org/search/${query} #kanji`,
    })
  } else if (source === 'pt') {
    definitions.push({
      name: 'conjugacao-pt',
      title: 'Conjugação.pt',
      link: `https://www.conjugacao.com.br/verbo-${query}/`,
    })
    definitions.push({
      name: 'coniugazione-pt',
      title: 'Coniugazione.it - portuguese',
      link: `https://www.coniugazione.it/portoghese/verbo/${query}.php`,
    })
  } else if (source === 'zh') {
    definitions.push({
      name: 'hanzicraft',
      title: 'Hanzicraft character dictionary',
      link: query ? `https://hanzicraft.com/character/${query}` : 'https://hanzicraft.com',
    })
    definitions.push({
      name: 'strokeorder',
      title: 'Stroke Order',
      link: query ? `https://www.strokeorder.com/chinese/${query}.html` : 'https://www.strokeorder.com',
    })
  }

  definitions.push({
    name: `wiktionary-${source}`,
    title: `Wiktionary - ${source}`,
    link: `https://${source}.wiktionary.org/wiki/` + query,
  })

  if (source !== 'en') {
    const langname = languageNames[source] || ''
    definitions.push({
      name: `wiktionary-en`,
      title: `Wiktionary (general)`,
      link: `https://en.wiktionary.org/wiki/${query}#${langname}`,
    })
  }
  return definitions
}